import styled, { css } from 'styled-components';
import { spacingOptions } from '../utils/utils';
import { asAnchor } from '../typography/anchors';

export const disabledButtonStyle = css`
	opacity: 0.5;
	pointer-events: none;
`;

export const primaryButtonStyle = css`
	color: #fff;
	background: ${props => props.theme.colors.primaryGradient};
	border-color: transparent;
	box-shadow: 0 0 24px 0 rgba(0,0,0,0.1);

	svg {
		color: #fff;
	}

	&:hover {
		background-color: #fff;
		border-color: ${props => props.theme.colors.primary};

		svg {
			color: #fff;
		}
	}

	&:focus {
		border-color: ${props => props.theme.colors.primaryDark};
	}
`;

export const secondaryButtonStyle = css`
	color: ${props => props.theme.colors.secondary};
	border-color: ${props => props.theme.colors.secondary};

	svg {
		color: ${props => props.theme.colors.secondary};
	}

	&:hover {
		background-color: ${props => props.theme.colors.secondary};
		color: #fff;

		svg {
			color: #fff;
		}
	}

	&:focus {
		border-color: ${props => props.theme.colors.secondaryDark};
	}
`;

export const tertiaryButtonStyle = css`
	color: #fff;
	background: ${props => props.theme.colors.primary};
	border-color: transparent;
	box-shadow: 0 0 24px 0 rgba(0,0,0,0.1);
	border-radius: 0;

	svg {
		color: #fff;
	}

	&:hover {
		background-color: ${props => props.theme.colors.primary};
		border-color: ${props => props.theme.colors.primary};

		svg {
			color: #fff;
		}
	}

	&:focus {
		border-color: ${props => props.theme.colors.primaryDark};
	}
`;

export const smallButtonStyle = css`
	height: ${props => props.theme.buttons.buttonHeightSmall};
	padding: 0 ${props => props.theme.buttons.buttonPaddingSmall};
	font-size: ${props => props.theme.buttons.buttonFontSizeSmall};
`;

export const largeButtonStyle = css`
	height: ${props => props.theme.buttons.buttonHeightLarge};
	padding: 0 ${props => props.theme.buttons.buttonPaddingLarge};
	font-size: ${props => props.theme.buttons.buttonFontSizeLarge};
`;

export const defaultButtonStyle = css`
	position: relative;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	flex: auto 0 0;
	height: ${props => props.theme.buttons.buttonHeight};
	padding: 0 ${props => props.theme.buttons.buttonPadding};
	color: ${props => props.theme.colors.black};
	background-color: #fff;
	font-size: ${props => props.theme.buttons.buttonFontSize};
	font-weight: ${props => props.theme.buttons.buttonFontWeight};
	border-radius: ${props => props.theme.buttons.buttonRadius};
	border: 1px solid ${props => props.theme.colors.borderColor};
	white-space: nowrap;
	text-overflow: ellipsis;
	cursor: pointer;
	transition-property: color, background-color, background, border, transform, opacity;
	transition-duration: 200ms;

	svg {
		color: ${props => props.theme.colors.gray};
		transition-property: color, background-color, border, transform, opacity;
		transition-duration: 200ms;

		&:first-child:not(:last-child) { margin-right: 2.5rem; }
		&:last-child:not(:first-child) { margin-left: 2.5rem; }
	}

	&:hover {
		border: 1px solid ${props => props.theme.colors.borderDark};
		background-color: #fafafa;
	}

	&:focus {
		border: 1px solid ${props => props.theme.colors.borderDark};
	}
`;

const headerNightButtonStyle = css`
	background: ${props => props.theme.buttons.headerNightButtonBackground};
	color: ${props => props.theme.buttons.headerNightButtonColor};
	border-color: ${props => props.theme.buttons.headerNightButtonBorderColor};
	transition: all ${props => props.theme.theming.nightDayTransitionDuration} ease;

	&:hover {
		background: ${props => props.theme.buttons.headerNightButtonBackgroundHover};
		color: ${props => props.theme.buttons.headerNightButtonColorHover};
		border-color: ${props => props.theme.buttons.headerNightButtonBorderColorHover};
	}
`;

export const StyledButton = styled.button`
	${defaultButtonStyle}
	${spacingOptions}
	${({ disabled }) => disabled && disabledButtonStyle}
	${({ loading }) => loading && disabledButtonStyle}
	${({ primary }) => primary && primaryButtonStyle}
	${({ secondary }) => secondary && secondaryButtonStyle}
	${({ tertiary }) => tertiary && tertiaryButtonStyle}
	${({ small }) => small && smallButtonStyle}
	${({ large }) => large && largeButtonStyle}
	${({ headerNight }) => headerNight && headerNightButtonStyle};
`;

export const LinkButton = styled.button`
	${defaultButtonStyle}
	${asAnchor}
	${spacingOptions}
	border: none;
	background: transparent;

	&:hover, &:focus {
		border: none;
		background: transparent;
	}
`;

const iconButtonStyles = css`
	padding: 0;

	svg, svg:first-child, svg:last-child {
		margin: 0;
	}
`;

const smallIconButtonStyle = css`
	height: ${props => props.theme.buttons.buttonHeightSmall};
	width: ${props => props.theme.buttons.buttonHeightSmall};
	font-size: ${props => props.theme.buttons.buttonFontSizeSmall};
`;

const largeIconButtonStyle = css`
	height: ${props => props.theme.buttons.buttonHeightLarge};
	width: ${props => props.theme.buttons.buttonHeightLarge};
	font-size: ${props => props.theme.buttons.buttonFontSizeLarge};
`;

export const IconButton = styled.button`
	width: ${props => props.theme.buttons.buttonHeight};
	${defaultButtonStyle};
	${iconButtonStyles};
	${spacingOptions};
	${({ disabled }) => disabled && disabledButtonStyle};
	${({ loading }) => loading && disabledButtonStyle};
	${({ primary }) => primary && primaryButtonStyle};
	${({ secondary }) => secondary && secondaryButtonStyle};
	${({ small }) => small && smallIconButtonStyle};
	${({ large }) => large && largeIconButtonStyle};
`;
